/**
 * §08 — Wave Director. Budget-driven (not timer-driven): every tick accrues threat from the
 * §8.2 per-minute curve, multiplied by the recovery envelope (§8.9), the boss-encounter
 * modifier (§8.7) and the hazard-phase modifier (§8.8). Accrued threat is spent on formation
 * spawn events (§8.3–§8.5) that pass the §8.4 composition rules and the §8.11 spawn-safety
 * rules.
 *
 * Determinism (§8.12): every draw consumes `w.rng.spawn`. The recovery schedule, the boss and
 * hazard schedule and the elite cadence are planned from the seed at run start; nothing reads
 * player state. There is no dynamic difficulty adjustment — difficulty is exactly this curve
 * times the selected Descent tier (`content/difficulty-modifiers.json`).
 */

import type { World } from './world';
import { POOL_CAPACITY } from './world';
import { wrapDeltaX, wrapDeltaY, wrapDist2, wrapX, wrapY } from './wrap';
import { cfg } from '../core/config';
import { AFFIXES, ELITE_MODIFIER, ENEMIES, PHASES, difficultyById, type EnemyDef } from '../core/content';
import {
  BOSS_INTRO_TICKS,
  bossReset,
  bossRestore,
  bossSnapshot,
  lastBossDefeatTick,
  spawnBoss,
  spawnEnemyExternal,
  type BossState,
} from './boss';
import {
  HAZARD_BRINE,
  HAZARD_TIDE,
  HAZARD_VENT,
  hazardEventActive,
  hazardReset,
  hazardRestore,
  hazardSnapshot,
  startHazardEvent,
  type HazardState,
} from './hazards';

// --- framing constants (spine §10, §10.2) ---

/**
 * Conservative camera half-extents used for the in-view spawn test. §10.2 frames 36 u
 * vertically (64 u at 16:9); these are inflated so a spawn is never mis-classified as
 * off-screen at the widest supported aspect.
 */
export const CAMERA_HALF_W = 39;
export const CAMERA_HALF_H = 22;
/** §8.11 / spine §10 — nothing spawns within 12 u of the player. Ever. */
export const SPAWN_SAFE_RADIUS = 12;
/** §8.11 — in-view spawns need a ≥ 1.5 s portal telegraph. */
export const PORTAL_TELEGRAPH_TICKS = 90;
/** §8.5 stream entry interval, 0.25 s between units. */
export const STREAM_ENTRY_TICKS = 15;
/** Slots held back from the enemy pool so boss summons and free spawns never starve (§8.4). */
export const POOL_RESERVE = 24;
/** Director evaluates a spawn event twice a second. */
export const SPAWN_EVAL_TICKS = 30;
/** §8.10 — director never idles: forced fodder after 4 s of nothing. */
export const IDLE_FORCE_TICKS = 240;
/** §8.10 — overpopulation guard trips after 20 s above 120% of the minute budget. */
export const OVERPOP_TRIP_TICKS = 1200;
/** §8.10 — stalemate break: a trash enemy alive 90 s self-terminates, in view only. */
export const STALEMATE_TICKS = 5400;
/** §8.7 — 20 s scripted recovery after each boss kill. */
export const POST_BOSS_RECOVERY_TICKS = 1200;
export const POST_BOSS_ENVELOPE = 0.5;
/** §8.9 — dips are ≥ 30%, i.e. envelope ≤ 0.70. */
export const RECOVERY_ENVELOPE = 0.7;
export const RECOVERY_PERIOD_MIN_S = 85;
export const RECOVERY_PERIOD_MAX_S = 95;
export const RECOVERY_DURATION_MIN_S = 10;
export const RECOVERY_DURATION_MAX_S = 12;
const MAX_RECOVERY_WINDOWS = 24;
/** §8.6 — elites are suppressed within ±60 s of a boss spawn. */
export const ELITE_BOSS_GUARD_TICKS = 3600;
/** §8.6 — elite spend ≤ 30% of any minute's budget. */
export const ELITE_SPEND_CAP_PCT = 30;
/** §8.13 — the run's last scheduled minute; the curve holds at the sovereign value after. */
export const MAX_MINUTE = 25;

// --- archetype classification (§8.4 composition families) ---

const enum Cls {
  Fodder = 0,
  Melee = 1,
  Ranged = 2,
  Support = 3,
  Leech = 4,
  Zone = 5,
  Curtain = 6,
}

const ROLE_CLASS: Record<string, Cls> = {
  basic_pursuer: Cls.Fodder,
  swarm_flock: Cls.Fodder,
  fast_interceptor: Cls.Melee,
  splitter: Cls.Melee,
  armored_bruiser: Cls.Melee,
  charger: Cls.Melee,
  ambush_predator: Cls.Melee,
  ranged_attacker: Cls.Ranged,
  ink_debuffer: Cls.Ranged,
  shielding_support: Cls.Support,
  spawner: Cls.Support,
  surge_drainer: Cls.Leech,
  area_denial: Cls.Zone,
  drifting_wall: Cls.Curtain,
};

const enum Form {
  Ring = 0,
  Stream = 1,
  Pincer = 2,
  Dispersed = 3,
}

const FORM_ID: Record<string, Form> = {
  ring: Form.Ring,
  stream: Form.Stream,
  pincer: Form.Pincer,
  dispersed: Form.Dispersed,
};

interface ArchetypeInfo {
  id: string;
  cls: Cls;
  threat: number;
  popBudget: number;
  openMin: number;
  closeMin: number;
  /** Bitmask of allowed formations. */
  forms: number;
  singleton: boolean;
}

const ARCH: ArchetypeInfo[] = [];
const ARCH_BY_ID = new Map<string, ArchetypeInfo>();

function buildArchetypes(): void {
  for (const raw of ENEMIES as readonly EnemyDef[]) {
    let forms = 0;
    let singleton = false;
    for (const f of raw.formation) {
      const id = FORM_ID[f];
      if (id === undefined) {
        singleton = true;
        continue;
      }
      forms |= 1 << id;
    }
    if (forms === 0) forms = 1 << Form.Dispersed;
    const cls = ROLE_CLASS[raw.role] !== undefined ? ROLE_CLASS[raw.role] : Cls.Melee;
    const info: ArchetypeInfo = {
      id: raw.id,
      cls,
      threat: raw.threat_score,
      popBudget: raw.pop_budget,
      openMin: raw.spawn_window_min[0],
      closeMin: raw.spawn_window_min[1],
      forms,
      singleton: singleton || cls === Cls.Curtain || cls === Cls.Support,
    };
    ARCH.push(info);
    ARCH_BY_ID.set(info.id, info);
  }
}
buildArchetypes();

// --- phase compilation (§8.13 / wave-phases.json) ---

interface EliteRule {
  everyS: number;
  jitterS: number;
  fromMin: number;
  paired: boolean;
}

interface CompiledPhase {
  id: string;
  startMin: number;
  endMin: number;
  /** Empty means "all unlocked archetypes". */
  allowed: string[];
  fodderMinPct: number;
  rangedMaxPct: number;
  supportCap: number;
  leechCap: number;
  zoneCap: number;
  curtainCap: number;
  formations: number;
  bossModifier: number;
  hazardModifier: number;
  elitesSuppressed: boolean;
  elite: EliteRule;
  pairedFromMin: number;
  budget: number[];
}

interface RawPhase {
  id: string;
  time_range_min: [number, number];
  budget_per_min: number[];
  boss_modifier?: number;
  hazard_modifier?: number;
  composition: {
    allowed_enemies: string[] | string;
    fodder_min_pct: number;
    ranged_max_pct: number;
    support_alive_cap: number;
    leech_cap?: number;
    zone_cap?: number;
    curtain_cap?: number;
  };
  formations_allowed: string[];
  scheduled: {
    elites: { at_min?: number; every_s?: number; jitter_s?: number; from_min?: number; paired?: boolean }[];
  };
  recovery: { flags: string[] };
}

const PHASE_TABLE: CompiledPhase[] = [];

function buildPhases(): void {
  for (const raw of PHASES as unknown as RawPhase[]) {
    let forms = 0;
    for (const f of raw.formations_allowed) {
      const id = FORM_ID[f];
      if (id !== undefined) forms |= 1 << id;
    }
    const comp = raw.composition;
    const elite: EliteRule = { everyS: 45, jitterS: 10, fromMin: raw.time_range_min[0], paired: false };
    let pairedFrom = Number.POSITIVE_INFINITY;
    for (const r of raw.scheduled.elites) {
      if (r.every_s !== undefined) {
        elite.everyS = r.every_s;
        elite.jitterS = r.jitter_s !== undefined ? r.jitter_s : 0;
        if (r.from_min !== undefined) elite.fromMin = r.from_min;
        if (r.paired === true) pairedFrom = raw.time_range_min[0];
      }
      if (r.paired === true && r.from_min !== undefined) pairedFrom = Math.min(pairedFrom, r.from_min);
    }
    PHASE_TABLE.push({
      id: raw.id,
      startMin: raw.time_range_min[0],
      endMin: raw.time_range_min[1],
      allowed: Array.isArray(comp.allowed_enemies) ? comp.allowed_enemies.slice() : [],
      fodderMinPct: comp.fodder_min_pct,
      rangedMaxPct: comp.ranged_max_pct,
      supportCap: comp.support_alive_cap,
      leechCap: comp.leech_cap !== undefined ? comp.leech_cap : 4,
      zoneCap: comp.zone_cap !== undefined ? comp.zone_cap : 6,
      curtainCap: comp.curtain_cap !== undefined ? comp.curtain_cap : 2,
      formations: forms === 0 ? 1 << Form.Dispersed : forms,
      bossModifier: raw.boss_modifier !== undefined ? raw.boss_modifier : 1,
      hazardModifier: raw.hazard_modifier !== undefined ? raw.hazard_modifier : 1,
      elitesSuppressed: raw.recovery.flags.indexOf('elites_suppressed') >= 0,
      elite,
      pairedFromMin: pairedFrom,
      budget: raw.budget_per_min.slice(),
    });
  }
}
buildPhases();

/** §8.2 per-minute threat budget, flattened from the phase table. */
export const BUDGET_PER_MIN: readonly number[] = (() => {
  const out = new Array<number>(MAX_MINUTE + 1).fill(0);
  for (const p of PHASE_TABLE) {
    for (let m = p.startMin; m <= MAX_MINUTE; m++) {
      const i = m - p.startMin;
      if (i < p.budget.length) out[m] = p.budget[i];
      else if (m < p.endMin) out[m] = p.budget[p.budget.length - 1];
    }
  }
  const last = PHASE_TABLE[PHASE_TABLE.length - 1];
  for (let m = last.startMin; m <= MAX_MINUTE; m++) out[m] = last.budget[last.budget.length - 1];
  return out;
})();

function phaseIndexForMinute(minuteFloat: number): number {
  for (let i = PHASE_TABLE.length - 1; i >= 0; i--) {
    if (minuteFloat >= PHASE_TABLE[i].startMin) return i;
  }
  return 0;
}

// --- scheduled encounters (§8.7 / §8.8) ---

interface ScheduleEntry {
  tick: number;
  /** 0 = boss, 1 = hazard event, 2 = scripted elite. */
  kind: number;
  id: string;
  durationS: number;
}

/** §8.7 boss timing, §8.8 hazard timing, §8.6 first elite — all fixed, all deterministic. */
export const SCHEDULE: readonly ScheduleEntry[] = [
  { tick: 120 * 60, kind: 2, id: 'enemy.driftfin', durationS: 0 },
  { tick: 300 * 60, kind: 0, id: 'boss.lantern-tyrant', durationS: 0 },
  { tick: 480 * 60, kind: 1, id: HAZARD_VENT, durationS: 45 },
  { tick: 600 * 60, kind: 0, id: 'boss.abyssal-warden', durationS: 0 },
  { tick: 780 * 60, kind: 1, id: HAZARD_BRINE, durationS: 45 },
  { tick: 900 * 60, kind: 0, id: 'boss.crown-medusa', durationS: 0 },
  { tick: 990 * 60, kind: 1, id: HAZARD_TIDE, durationS: 60 },
  { tick: 1200 * 60, kind: 0, id: 'boss.hadal-sovereign', durationS: 0 },
];

const BOSS_TICKS: readonly number[] = SCHEDULE.filter((s) => s.kind === 0).map((s) => s.tick);

// --- AMD-34 wave envelope (legible pacing over the §8.1 budget) ---

/**
 * Wave length. 30 s is not a taste call: every spine §4 anchor (2:00, 5:00, 8:00, 10:00,
 * 13:00, 15:00, 16:00, 16:30, 18:00, 19:00, 19:30, 20:00) is a whole multiple of 30 s, so
 * every scheduled encounter in {@link SCHEDULE} lands on a wave boundary without moving a
 * single anchor. Two waves tile a minute exactly, which is what keeps the §8.2 per-minute
 * accrual identical to the pre-wave director (see {@link waveEnvelopeAt}).
 */
export const WAVE_TICKS = 1800;
/** The wave lands: 0–4 s at double the minute's baseline pressure. */
export const WAVE_ONSET_TICKS = 240;
/** The fight: 4–22 s at baseline. The lull runs from here to the wave boundary. */
export const WAVE_SUSTAIN_END_TICKS = 1320;
/** Last 3 s of the lull — the incoming-wave telegraph the HUD announces on. */
export const WAVE_MUSTER_TICKS = 180;
export const WAVE_ONSET_ENVELOPE = 2;
export const WAVE_SUSTAIN_ENVELOPE = 1;
/**
 * The lull keeps accruing at half rate while {@link inWaveLull} holds every spawn. That
 * banked half-rate mass is what makes the next wave *land* instead of merely resuming: the
 * onset opens with 4 s of budget already in hand on top of its own 2× rate.
 */
export const WAVE_LULL_ENVELOPE = 0.5;
/** Bosses open every tenth wave — 11, 21, 31, 41 — at the §8.7 times. */
export const BOSS_WAVE_PERIOD = 10;
/**
 * §8.14 G2 floor inside a lull. G2 forbids empty downtime *longer than* 8 s, so an empty
 * field may coast the full 8 s lull; the normal 4 s force would shatter it. Anything past
 * the lull is back on {@link IDLE_FORCE_TICKS}.
 */
export const LULL_IDLE_FORCE_TICKS = 480;
/** Full 20-minute run: 40 complete waves, with the final boss opening wave 41. */
export const WAVES_PER_RUN = 40;

export type WaveSegment = 'onset' | 'sustain' | 'lull' | 'muster';

/** 1-based wave number. Wave N covers [(N-1)·30 s, N·30 s). Pure function of the tick. */
export function waveAt(tick: number): number {
  return Math.floor(tick / WAVE_TICKS) + 1;
}

/** Ticks elapsed inside the current wave. */
export function waveOffset(tick: number): number {
  return tick - Math.floor(tick / WAVE_TICKS) * WAVE_TICKS;
}

/** First tick of the next wave. */
export function waveEndTick(tick: number): number {
  return (Math.floor(tick / WAVE_TICKS) + 1) * WAVE_TICKS;
}

export function waveSegmentAt(tick: number): WaveSegment {
  const o = waveOffset(tick);
  if (o < WAVE_ONSET_TICKS) return 'onset';
  if (o < WAVE_SUSTAIN_END_TICKS) return 'sustain';
  if (o < WAVE_TICKS - WAVE_MUSTER_TICKS) return 'lull';
  return 'muster';
}

/** True through both the `lull` and `muster` beats — the spawn-free tail of the wave. */
export function inWaveLull(tick: number): boolean {
  return waveOffset(tick) >= WAVE_SUSTAIN_END_TICKS;
}

/**
 * Intra-wave accrual shape. Its mean over one wave is exactly 1:
 * `4 × 2 + 18 × 1 + 8 × 0.5 = 30`. That identity is why overlaying waves on the §8.2 curve
 * does not move a single minute's budget — it only redistributes it inside the minute.
 */
export function waveEnvelopeAt(tick: number): number {
  const o = waveOffset(tick);
  if (o < WAVE_ONSET_TICKS) return WAVE_ONSET_ENVELOPE;
  if (o < WAVE_SUSTAIN_END_TICKS) return WAVE_SUSTAIN_ENVELOPE;
  return WAVE_LULL_ENVELOPE;
}

/** Waves opened by a §8.7 encounter. Derived from {@link SCHEDULE}, never hardcoded. */
export function isBossWave(wave: number): boolean {
  return BOSS_TICKS.indexOf((wave - 1) * WAVE_TICKS) >= 0;
}

function segmentEndTick(tick: number): number {
  const base = Math.floor(tick / WAVE_TICKS) * WAVE_TICKS;
  const o = tick - base;
  if (o < WAVE_ONSET_TICKS) return base + WAVE_ONSET_TICKS;
  if (o < WAVE_SUSTAIN_END_TICKS) return base + WAVE_SUSTAIN_END_TICKS;
  if (o < WAVE_TICKS - WAVE_MUSTER_TICKS) return base + WAVE_TICKS - WAVE_MUSTER_TICKS;
  return base + WAVE_TICKS;
}

// --- per-world state ---

/** A.6 — serializable director state for pause/resume and replay. */
export interface DirectorState {
  version: number;
  bank: number;
  phase_index: number;
  minute_index: number;
  minute_spend: number;
  minute_elite_spend: number;
  minute_ranged_spend: number;
  next_spawn_tick: number;
  next_elite_tick: number;
  last_affix: string;
  schedule_cursor: number;
  planned_start: number[];
  planned_end: number[];
  planned_env: number[];
  planned_count: number;
  planned: number;
  post_boss_until: number;
  boss_active_until: number;
  spawn_pause_until: number;
  overpop_since: number;
  last_activity_tick: number;
  next_stalemate_scan: number;
  spawn_serial: number;
  boss: BossState;
  hazards: HazardState;
}

interface DirectorRuntime {
  bank: number;
  phase_index: number;
  minute_index: number;
  minute_spend: number;
  minute_elite_spend: number;
  minute_ranged_spend: number;
  next_spawn_tick: number;
  next_elite_tick: number;
  last_affix: string;
  schedule_cursor: number;
  planned_start: number[];
  planned_end: number[];
  planned_env: number[];
  planned_count: number;
  planned: boolean;
  post_boss_until: number;
  boss_active_until: number;
  spawn_pause_until: number;
  overpop_since: number;
  last_activity_tick: number;
  next_stalemate_scan: number;
  spawn_serial: number;
  /** Per-minute realized spend, for the V1 curve-conformance validator. */
  spend_by_minute: number[];
}

const RUNTIME = new WeakMap<World, DirectorRuntime>();

function runtime(w: World): DirectorRuntime {
  let st = RUNTIME.get(w);
  if (st === undefined) {
    st = {
      bank: 0,
      phase_index: 0,
      minute_index: 0,
      minute_spend: 0,
      minute_elite_spend: 0,
      minute_ranged_spend: 0,
      next_spawn_tick: 0,
      next_elite_tick: 0,
      last_affix: '',
      schedule_cursor: 0,
      planned_start: new Array<number>(MAX_RECOVERY_WINDOWS).fill(0),
      planned_end: new Array<number>(MAX_RECOVERY_WINDOWS).fill(0),
      planned_env: new Array<number>(MAX_RECOVERY_WINDOWS).fill(1),
      planned_count: 0,
      planned: false,
      post_boss_until: 0,
      boss_active_until: 0,
      spawn_pause_until: 0,
      overpop_since: -1,
      last_activity_tick: 0,
      next_stalemate_scan: 0,
      spawn_serial: 0,
      spend_by_minute: new Array<number>(MAX_MINUTE + 2).fill(0),
    };
    RUNTIME.set(w, st);
  }
  return st;
}

// --- module scratch (preallocated; nothing in the tick path allocates) ---

const candIdx = new Int32Array(64);
const candWeight = new Float64Array(64);
const posX = new Float64Array(32);
const posY = new Float64Array(32);
const posTele = new Int32Array(32);

// --- public queries ---

export function currentPhaseId(w: World): string {
  return PHASE_TABLE[runtime(w).phase_index].id;
}

/**
 * Measurement hook (`tools/econ-probe.ts`). Scales the §8.2 curve so a sweep can ask what a
 * difficulty ramp actually does to survival, XP income and level pace, with nothing else
 * moving. Ships at 1 and is not reachable from the UI — the shape `setOverpopGuardSuspended`
 * and AMD-24c's `setHuntRampEnabled` already use.
 */
let threatBudgetScale = 1;

export function setThreatBudgetScale(x: number): void {
  threatBudgetScale = x > 0 ? x : 1;
}

/** §8.2 budget for a minute, after the Descent tier's `threat_budget` multiplier. */
export function budgetForMinute(w: World, minute: number): number {
  const m = minute < 0 ? 0 : minute > MAX_MINUTE ? MAX_MINUTE : minute;
  return BUDGET_PER_MIN[m] * difficultyMult(w, 'threat_budget') * threatBudgetScale;
}

/** Current recovery envelope (§8.9). 1.0 outside a dip. Does not include the wave shape. */
export function currentEnvelope(w: World): number {
  return envelope(w, runtime(w));
}

/** AMD-34 — the run's headline number. Same seed, same clock, same wave. */
export function currentWave(w: World): number {
  return waveAt(w.tick);
}

/** 0 → 1 across the current wave. The HUD's wave progress bar reads this. */
export function waveProgress(w: World): number {
  return waveOffset(w.tick) / WAVE_TICKS;
}

export function currentWaveSegment(w: World): WaveSegment {
  return waveSegmentAt(w.tick);
}

/** Seconds left in the current beat — the countdown into the next wave during `muster`. */
export function waveSegmentRemainingS(w: World): number {
  return (segmentEndTick(w.tick) - w.tick) / 60;
}

/** AMD-34 wave shape alone (§8.9 recovery excluded). */
export function currentWaveEnvelope(w: World): number {
  return waveEnvelopeAt(w.tick);
}

/**
 * What the player actually feels: §8.9 recovery × AMD-34 wave shape. Audio and post
 * effects should drive off this, not off {@link currentEnvelope}.
 */
export function currentIntensity(w: World): number {
  return envelope(w, runtime(w)) * waveEnvelopeAt(w.tick);
}

/** Threat currently alive — the §8.4 composition denominator. */
export function aliveThreat(w: World): number {
  const list = w.enemies.liveIndices();
  let sum = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.is_boss) continue;
    sum += e.threat_score;
  }
  return sum;
}

/**
 * AMD-45 — threat close enough to the player to be pressure, measured the short way round
 * the torus. 40 u is the outer edge of the §8.5 dispersed spawn annulus: a body inside it
 * either reached the player or was placed there, and a body outside it is one the player
 * has left behind.
 */
export const OVERPOP_ENGAGE_RADIUS = 40;

/**
 * §8.10's overpopulation denominator.
 *
 * The guard exists to relieve a player the field has buried. It used to read
 * {@link aliveThreat} — every body anywhere on 40,000 u² of torus — which on a wall-less
 * arena means something entirely different: a pilot who runs in a straight line and kills
 * nothing accumulates a comet tail that never closes, and that tail alone pushed alive
 * threat past 120% of the minute's budget and switched the director off. Measured on the
 * shipped build, the guard held every trash spawn for **43-46% of a flat-out kiting run and
 * 0% of every other protocol**. The §8.2 ramp was being refunded, in full, to the one
 * playstyle it was aimed at, and to nobody else.
 *
 * Counting only what is inside {@link OVERPOP_ENGAGE_RADIUS} restores the guard's stated
 * intent. A player genuinely swarmed still gets the relief; a tail 80 u behind buys none.
 * Population safety is unaffected — {@link enemyCap} and each archetype's `pop_budget`
 * bound the count, and they always did.
 */
export function engagedThreat(w: World): number {
  const list = w.enemies.liveIndices();
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  const r2 = OVERPOP_ENGAGE_RADIUS * OVERPOP_ENGAGE_RADIUS;
  let sum = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.is_boss) continue;
    const dx = wrapDeltaX(e.pos.x - px);
    const dy = wrapDeltaY(e.pos.y - py);
    if (dx * dx + dy * dy > r2) continue;
    sum += e.threat_score;
  }
  return sum;
}

/** True while §8.10's overpopulation guard is holding every trash spawn. Diagnostic. */
export function spawnPaused(w: World): boolean {
  return w.tick < runtime(w).spawn_pause_until;
}

/** Realized spend for a minute — validator V1 reads this. */
export function spendInMinute(w: World, minute: number): number {
  const st = runtime(w);
  return minute >= 0 && minute < st.spend_by_minute.length ? st.spend_by_minute[minute] : 0;
}

/** Live enemy cap after the AMD-2 tier budget and the boss-summon reserve. */
export function enemyCap(w: World): number {
  const tierCap = cfg.get('budget.max_enemies');
  const poolCap = POOL_CAPACITY.enemies - POOL_RESERVE;
  return tierCap < poolCap ? tierCap : poolCap;
}

function difficultyMult(w: World, key: string): number {
  const tier = difficultyById.get(w.difficultyId);
  if (tier === undefined) return 1;
  const mults = (tier as unknown as { stat_multipliers?: Record<string, number> }).stat_multipliers;
  if (mults === undefined || mults[key] === undefined) return 1;
  return mults[key];
}

function difficultyComp(w: World, key: string): number {
  const tier = difficultyById.get(w.difficultyId);
  if (tier === undefined) return 0;
  const c = (tier as unknown as { composition_changes?: Record<string, number> }).composition_changes;
  if (c === undefined || c[key] === undefined) return 0;
  return c[key];
}

// --- step ---

/** Step 3 — phase advance, schedule, budget accrual, elite cadence, spawn events. */
export function stepDirector(w: World): void {
  if (w.outcome !== 'running') return;
  const st = runtime(w);
  if (!st.planned) planRun(w, st);

  const tick = w.tick;
  const minute = Math.floor(tick / 3600);
  if (minute !== st.minute_index) {
    st.minute_index = minute;
    st.minute_spend = 0;
    st.minute_elite_spend = 0;
    st.minute_ranged_spend = 0;
  }

  emitWaveSignals(w, tick);
  advancePhase(w, st, tick / 3600);
  runSchedule(w, st, tick);
  accrue(w, st, minute);
  antiStall(w, st, tick, minute);
  maybeSpawnElite(w, st, tick, minute);
  maybeSpawnWave(w, st, tick, minute);
}

/** §8.9 — dips are planned at run start from the seed, never triggered by player state. */
function planRun(w: World, st: DirectorRuntime): void {
  st.planned = true;
  let t = 0;
  let n = 0;
  while (n < MAX_RECOVERY_WINDOWS - 1) {
    const period = RECOVERY_PERIOD_MIN_S + w.rng.spawn.next() * (RECOVERY_PERIOD_MAX_S - RECOVERY_PERIOD_MIN_S);
    const duration = RECOVERY_DURATION_MIN_S + w.rng.spawn.next() * (RECOVERY_DURATION_MAX_S - RECOVERY_DURATION_MIN_S);
    t += period;
    if (t > 1200) break;
    st.planned_start[n] = Math.round(t * 60);
    st.planned_end[n] = Math.round((t + duration) * 60);
    st.planned_env[n] = RECOVERY_ENVELOPE;
    n++;
  }
  // §8.9 pinned pre-final calm: 19:00–19:30 at ×0.5.
  st.planned_start[n] = 1140 * 60;
  st.planned_end[n] = 1170 * 60;
  st.planned_env[n] = 0.5;
  n++;
  st.planned_count = n;
  st.next_elite_tick = 120 * 60;
  st.last_activity_tick = 0;
}

/**
 * AMD-34 — the sim's only wave signal. Boundary detection is a tick comparison rather than
 * stored state, so a snapshot restored mid-run resumes on the same wave with no bookkeeping
 * to get wrong (A.6) and a replay emits the identical sequence (§8.12).
 */
function emitWaveSignals(w: World, tick: number): void {
  const off = waveOffset(tick);
  const isBoundary = off === 0;
  if (
    !isBoundary &&
    off !== WAVE_ONSET_TICKS &&
    off !== WAVE_SUSTAIN_END_TICKS &&
    off !== WAVE_TICKS - WAVE_MUSTER_TICKS
  ) {
    return;
  }
  const wave = waveAt(tick);
  if (isBoundary) {
    w.bus.emit('wave_change', {
      wave,
      boss_wave: isBossWave(wave),
      phase_id: PHASE_TABLE[phaseIndexForMinute(tick / 3600)].id,
      duration_s: WAVE_TICKS / 60,
    });
  }
  w.bus.emit('wave_phase', {
    wave,
    segment: waveSegmentAt(tick),
    remaining_s: (segmentEndTick(tick) - tick) / 60,
  });
}

function advancePhase(w: World, st: DirectorRuntime, minuteFloat: number): void {
  const idx = phaseIndexForMinute(minuteFloat);
  if (idx === st.phase_index) return;
  st.phase_index = idx;
  w.bus.emit('phase_change', { phase_id: PHASE_TABLE[idx].id });
}

function runSchedule(w: World, st: DirectorRuntime, tick: number): void {
  while (st.schedule_cursor < SCHEDULE.length && tick >= SCHEDULE[st.schedule_cursor].tick) {
    const entry = SCHEDULE[st.schedule_cursor];
    st.schedule_cursor++;
    if (entry.kind === 0) {
      const e = spawnBoss(w, entry.id);
      if (e !== null) st.boss_active_until = 0x7fffffff;
    } else if (entry.kind === 1) {
      startHazardEvent(w, entry.id, entry.durationS);
    } else {
      spawnScriptedElite(w, st, entry.id);
    }
    st.last_activity_tick = tick;
  }
  const defeat = lastBossDefeatTick(w);
  if (defeat >= 0 && defeat > st.post_boss_until - POST_BOSS_RECOVERY_TICKS) {
    st.post_boss_until = defeat + POST_BOSS_RECOVERY_TICKS;
    st.boss_active_until = 0;
  }
}

function bossAlive(w: World): boolean {
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    if (w.enemies.slots[list[i]].is_boss) return true;
  }
  return false;
}

function envelope(w: World, st: DirectorRuntime): number {
  const tick = w.tick;
  let e = 1;
  for (let i = 0; i < st.planned_count; i++) {
    if (tick >= st.planned_start[i] && tick < st.planned_end[i] && st.planned_env[i] < e) e = st.planned_env[i];
  }
  if (tick < st.post_boss_until && POST_BOSS_ENVELOPE < e) e = POST_BOSS_ENVELOPE;
  return e;
}

/** §8.1 — accrual(t) × envelope × boss_modifier × hazard_modifier, banked, capped at 2× budget. */
function accrue(w: World, st: DirectorRuntime, minute: number): void {
  const phase = PHASE_TABLE[st.phase_index];
  const budget = budgetForMinute(w, minute);
  let rate = budget / 3600;
  rate *= envelope(w, st);
  // AMD-34. Mean 1 over every wave and two waves per minute, so `budget` still lands
  // whole inside its minute — the wave only decides when in the minute it arrives.
  rate *= waveEnvelopeAt(w.tick);
  if (bossAlive(w)) rate *= phase.bossModifier;
  if (hazardEventActive(w)) rate *= phase.hazardModifier;
  // AMD-51 — swarm-surge scales the accrual, never the budget table.
  rate *= w.anomalyThreatScale;
  st.bank += rate;
  const cap = budget * 2;
  if (st.bank > cap) st.bank = cap;
}

// --- §8.10 anti-stall ---

/**
 * §C.1's `bench.suspend_overpop_guard`. The benchmark holds 500 enemies alive — far
 * above 120% of any minute's §8.2 budget — so with the guard armed the director would
 * stop spawning 20 s into the measured window and the scene would drain. Off in every
 * real run; the harness sets it and the artifact manifest records it.
 */
let overpopGuardSuspended = false;

export function setOverpopGuardSuspended(v: boolean): void {
  overpopGuardSuspended = v;
}

export function isOverpopGuardSuspended(): boolean {
  return overpopGuardSuspended;
}

function antiStall(w: World, st: DirectorRuntime, tick: number, minute: number): void {
  // Overpopulation guard. AMD-45 — measured inside the engagement radius; see `engagedThreat`.
  const budget = budgetForMinute(w, minute);
  const alive = engagedThreat(w);
  if (overpopGuardSuspended) {
    st.overpop_since = -1;
    if (st.spawn_pause_until === 0x7fffffff) st.spawn_pause_until = 0;
  } else if (alive > budget * 1.2) {
    if (st.overpop_since < 0) st.overpop_since = tick;
    else if (tick - st.overpop_since > OVERPOP_TRIP_TICKS) st.spawn_pause_until = 0x7fffffff;
  } else {
    st.overpop_since = -1;
    if (st.spawn_pause_until === 0x7fffffff && alive <= budget) st.spawn_pause_until = 0;
  }

  // The §8.10 stalemate break (90 s alive, in view only) is implemented enemy-side by R3
  // in `src/sim/enemies.ts`; duplicating it here would double-emit the death event.

  // G2 — never more than 8 s of empty downtime.
  if (w.enemies.count > 0 || bossAlive(w) || hazardEventActive(w)) st.last_activity_tick = tick;
  const idleLimit = inWaveLull(tick) ? LULL_IDLE_FORCE_TICKS : IDLE_FORCE_TICKS;
  if (tick - st.last_activity_tick >= idleLimit && tick >= w.cinematicUntil) {
    forceFodderStream(w, st, tick);
    st.last_activity_tick = tick;
  }
}

function forceFodderStream(w: World, st: DirectorRuntime, tick: number): void {
  const info = ARCH_BY_ID.get('enemy.driftfin');
  if (info === undefined) return;
  const n = buildFormation(w, Form.Stream, 6, tick);
  emitFormation(w, st, info, n, false, '', '');
}

// --- §8.6 elite cadence ---

function spawnScriptedElite(w: World, st: DirectorRuntime, defId: string): void {
  const info = ARCH_BY_ID.get(defId);
  if (info === undefined) return;
  const n = buildFormation(w, Form.Dispersed, 1, w.tick);
  const cost = info.threat * ELITE_MODIFIER.multipliers.threat_cost + affixCost('affix.swift');
  emitFormation(w, st, info, n, true, 'affix.swift', '');
  st.bank -= cost;
  if (st.bank < 0) st.bank = 0;
  st.minute_elite_spend += cost;
  st.last_affix = 'affix.swift';
  st.next_elite_tick = w.tick + Math.round(45 * 60);
}

function affixCost(id: string): number {
  for (const a of AFFIXES) if (a.id === id) return a.threat_cost;
  return 0;
}

function nearBoss(tick: number): boolean {
  for (let i = 0; i < BOSS_TICKS.length; i++) {
    const d = tick - BOSS_TICKS[i];
    if (d > -ELITE_BOSS_GUARD_TICKS && d < ELITE_BOSS_GUARD_TICKS) return true;
  }
  return false;
}

function maybeSpawnElite(w: World, st: DirectorRuntime, tick: number, minute: number): void {
  if (tick < w.cinematicUntil || tick < st.spawn_pause_until) return;
  // AMD-34 — an elite due inside the lull waits for the wave to open and arrives with it.
  if (inWaveLull(tick)) {
    if (tick >= st.next_elite_tick) st.next_elite_tick = waveEndTick(tick);
    return;
  }
  const phase = PHASE_TABLE[st.phase_index];
  if (phase.elitesSuppressed || nearBoss(tick)) {
    if (tick >= st.next_elite_tick) st.next_elite_tick = tick + Math.round(phase.elite.everyS * 60);
    return;
  }
  if (tick < st.next_elite_tick) return;
  const minuteFloat = tick / 3600;
  if (minuteFloat < phase.elite.fromMin) return;

  const info = pickEliteArchetype(w, st, phase, minuteFloat);
  if (info === null) {
    st.next_elite_tick = tick + 60;
    return;
  }
  const paired = minuteFloat >= phase.pairedFromMin;
  const affixCount = Math.max(1, difficultyComp(w, 'elite_affix_count') || ELITE_MODIFIER.affix_count.default);
  const affixA = drawAffix(w, st.last_affix);
  const affixB = affixCount >= 2 ? drawAffix(w, affixA) : '';
  st.last_affix = affixA;

  const units = paired ? 2 : 1;
  const unitCost = info.threat * ELITE_MODIFIER.multipliers.threat_cost + affixCost(affixA) + affixCost(affixB);
  const cost = unitCost * units;
  const capPct = difficultyComp(w, 'elite_spend_cap_pct') || ELITE_SPEND_CAP_PCT;
  const budget = budgetForMinute(w, minute);
  if (st.minute_elite_spend + cost > (budget * capPct) / 100 || st.bank < cost) {
    st.next_elite_tick = tick + 60;
    return;
  }
  if (w.enemies.count + units > enemyCap(w)) {
    st.next_elite_tick = tick + 60;
    return;
  }

  const n = buildFormation(w, Form.Dispersed, units, tick);
  const spawned = emitFormation(w, st, info, n, true, affixA, affixB);
  if (spawned === 0) {
    st.next_elite_tick = tick + 60;
    return;
  }
  st.bank -= unitCost * spawned;
  if (st.bank < 0) st.bank = 0;
  st.minute_elite_spend += unitCost * spawned;
  st.last_activity_tick = tick;

  const freq = difficultyMult(w, 'elite_frequency');
  const jitter = (w.rng.spawn.next() * 2 - 1) * phase.elite.jitterS;
  const interval = Math.max(10, (phase.elite.everyS + jitter) / (freq > 0 ? freq : 1));
  st.next_elite_tick = tick + Math.round(interval * 60);
}

/** §8.6 — archetype drawn from the current phase pool weighted by fodder share. */
function pickEliteArchetype(w: World, st: DirectorRuntime, phase: CompiledPhase, minuteFloat: number): ArchetypeInfo | null {
  let n = 0;
  let total = 0;
  for (let i = 0; i < ARCH.length && n < candIdx.length; i++) {
    const a = ARCH[i];
    if (!allowedInPhase(phase, a, minuteFloat)) continue;
    if (a.cls === Cls.Support || a.cls === Cls.Curtain) continue;
    const weight = a.cls === Cls.Fodder ? 3 : 1;
    candIdx[n] = i;
    candWeight[n] = weight;
    total += weight;
    n++;
  }
  if (n === 0) return null;
  let roll = w.rng.spawn.next() * total;
  for (let i = 0; i < n; i++) {
    roll -= candWeight[i];
    if (roll <= 0) return ARCH[candIdx[i]];
  }
  return ARCH[candIdx[n - 1]];
}

function drawAffix(w: World, avoid: string): string {
  for (let attempt = 0; attempt < 8; attempt++) {
    const a = AFFIXES[Math.floor(w.rng.spawn.next() * AFFIXES.length)];
    if (a.id !== avoid) return a.id;
  }
  return AFFIXES[0].id;
}

// --- §8.3–§8.5 trash spawn events ---

/**
 * §8.6's elite cadence is a schedule, not a bid.
 *
 * Elites and trash draw on the same bank, and `maybeSpawnWave` will spend it down to the
 * price of a single fodder unit twice a second. An elite costs `4 × base threat + affix`
 * (§8.3) and doubles once §8.6's paired elites start at 12:00 — 100–250 threat late-run —
 * so `maybeSpawnElite`'s `st.bank < cost` guard was never satisfied and the elite it had
 * already scheduled simply retried, one second at a time, until the run ended. Measured:
 * **2 elite spawns in a 20-minute run** against the ~25 §8.6 schedules, and the 2:00 one
 * of those is the scripted entry in {@link SCHEDULE} that spends nothing.
 *
 * The reserve is the *unspent* part of the minute's §8.6 elite allowance, held back from
 * trash only in the lead-up to a scheduled elite. It never raises the total spend — §8.4's
 * budget still bounds both — it only stops trash from eating the elite's share first.
 */
const ELITE_RESERVE_LEAD_TICKS = 15 * 60;

function eliteReserve(w: World, st: DirectorRuntime, phase: CompiledPhase, tick: number, minute: number): number {
  if (phase.elitesSuppressed || nearBoss(tick)) return 0;
  if (tick / 3600 < phase.elite.fromMin) return 0;
  if (st.next_elite_tick - tick > ELITE_RESERVE_LEAD_TICKS) return 0;
  const capPct = difficultyComp(w, 'elite_spend_cap_pct') || ELITE_SPEND_CAP_PCT;
  const room = (budgetForMinute(w, minute) * capPct) / 100 - st.minute_elite_spend;
  return room > 0 ? room : 0;
}

function allowedInPhase(phase: CompiledPhase, a: ArchetypeInfo, minuteFloat: number): boolean {
  if (minuteFloat < a.openMin || minuteFloat > a.closeMin) return false;
  if (phase.allowed.length === 0) return true;
  return phase.allowed.indexOf(a.id) >= 0;
}

function maybeSpawnWave(w: World, st: DirectorRuntime, tick: number, minute: number): void {
  if (tick < w.cinematicUntil) return;
  // AMD-34 — the lull is a real stop, not a slowdown. `next_spawn_tick` is deliberately left
  // in the past so the first tick of the next wave spawns on the frame the wave lands.
  if (inWaveLull(tick)) return;
  if (tick < st.spawn_pause_until) return;
  if (tick < st.next_spawn_tick) return;
  st.next_spawn_tick = tick + SPAWN_EVAL_TICKS;

  const cap = enemyCap(w);
  if (w.enemies.count >= cap) return;

  const phase = PHASE_TABLE[st.phase_index];
  const minuteFloat = tick / 3600;
  const budget = budgetForMinute(w, minute);
  // Everything trash may legally spend this evaluation. See `eliteReserve`.
  const spendable = st.bank - eliteReserve(w, st, phase, tick, minute);

  // §8.4: up to 3 re-rolls, then the cheapest legal fodder spawn.
  for (let attempt = 0; attempt < 4; attempt++) {
    const info = pickArchetype(w, st, phase, minuteFloat, attempt === 3);
    if (info === null) return;
    if (spendable < info.threat) return;

    const form = pickFormation(w, phase, info);
    let count = pickCount(w, form, info);
    const affordable = Math.floor(spendable / info.threat);
    if (count > affordable) count = affordable;
    count = limitByPopulation(w, info, count, cap);
    if (count < 1) continue;
    count = limitByComposition(w, st, phase, info, count, budget);
    if (count < 1) continue;

    const n = buildFormation(w, form, count, tick);
    if (n === 0) continue;
    const spawned = emitFormation(w, st, info, n, false, '', '');
    if (spawned === 0) continue;
    const cost = spawned * info.threat;
    st.bank -= cost;
    if (st.bank < 0) st.bank = 0;
    st.minute_spend += cost;
    st.spend_by_minute[minute < st.spend_by_minute.length ? minute : st.spend_by_minute.length - 1] += cost;
    if (info.cls === Cls.Ranged) st.minute_ranged_spend += cost;
    st.last_activity_tick = tick;
    return;
  }
}

function pickArchetype(w: World, st: DirectorRuntime, phase: CompiledPhase, minuteFloat: number, forceFodder: boolean): ArchetypeInfo | null {
  const alive = aliveThreat(w);
  const fodderShare = alive > 0 ? fodderThreat(w) / alive : 1;
  const floorPct = (phase.fodderMinPct + difficultyComp(w, 'fodder_min_pct_delta')) / 100;
  const bossFloor = bossAlive(w) ? 0.25 : floorPct;
  const needFodder = forceFodder || fodderShare < bossFloor;

  let n = 0;
  let total = 0;
  for (let i = 0; i < ARCH.length && n < candIdx.length; i++) {
    const a = ARCH[i];
    if (!allowedInPhase(phase, a, minuteFloat)) continue;
    if (needFodder && a.cls !== Cls.Fodder) continue;
    let weight = 1;
    if (a.cls === Cls.Fodder) weight = 4;
    else if (a.cls === Cls.Melee) weight = 2;
    else if (a.cls === Cls.Ranged) weight = 1.2;
    else weight = 0.6;
    candIdx[n] = i;
    candWeight[n] = weight;
    total += weight;
    n++;
  }
  if (n === 0) return null;
  let roll = w.rng.spawn.next() * total;
  for (let i = 0; i < n; i++) {
    roll -= candWeight[i];
    if (roll <= 0) return ARCH[candIdx[i]];
  }
  return ARCH[candIdx[n - 1]];
}

function fodderThreat(w: World): number {
  const list = w.enemies.liveIndices();
  let sum = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.is_boss) continue;
    const a = ARCH_BY_ID.get(e.def_id);
    if (a !== undefined && a.cls === Cls.Fodder) sum += e.threat_score;
  }
  return sum;
}

function classAlive(w: World, cls: Cls): number {
  const list = w.enemies.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (e.is_boss) continue;
    const a = ARCH_BY_ID.get(e.def_id);
    if (a !== undefined && a.cls === cls) n++;
  }
  return n;
}

function archetypeAlive(w: World, defId: string): number {
  const list = w.enemies.liveIndices();
  let n = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.is_boss && e.def_id === defId) n++;
  }
  return n;
}

function pickFormation(w: World, phase: CompiledPhase, info: ArchetypeInfo): Form {
  const mask = phase.formations & info.forms;
  if (mask === 0) return Form.Dispersed;
  let n = 0;
  for (let f = 0; f < 4; f++) if ((mask & (1 << f)) !== 0) candIdx[n++] = f;
  return candIdx[Math.floor(w.rng.spawn.next() * n)] as Form;
}

/** §8.5 formation sizes. Expensive archetypes come in small groups. */
function pickCount(w: World, form: Form, info: ArchetypeInfo): number {
  if (info.singleton) return 1;
  let lo: number;
  let hi: number;
  if (form === Form.Ring) {
    lo = 6;
    hi = 14;
  } else if (form === Form.Stream) {
    lo = 8;
    hi = 16;
  } else if (form === Form.Pincer) {
    lo = 10;
    hi = 16;
  } else {
    lo = 3;
    hi = 10;
  }
  if (info.threat >= 18) {
    lo = 1;
    hi = 3;
  } else if (info.threat >= 9) {
    lo = Math.max(2, lo >> 1);
    hi = Math.max(lo, hi >> 1);
  }
  const n = lo + Math.floor(w.rng.spawn.next() * (hi - lo + 1));
  return n > posX.length ? posX.length : n;
}

function limitByPopulation(w: World, info: ArchetypeInfo, count: number, cap: number): number {
  const room = cap - w.enemies.count;
  if (room < count) count = room;
  const archRoom = info.popBudget - archetypeAlive(w, info.id);
  if (archRoom < count) count = archRoom;
  return count < 0 ? 0 : count;
}

/** §8.4 composition rules; returns the largest legal count, 0 when the archetype is illegal now. */
function limitByComposition(w: World, st: DirectorRuntime, phase: CompiledPhase, info: ArchetypeInfo, count: number, budget: number): number {
  const minuteFloat = w.tick / 3600;
  if (info.cls === Cls.Ranged) {
    const capPct = phase.rangedMaxPct + difficultyComp(w, 'ranged_max_pct_delta');
    const room = (budget * capPct) / 100 - st.minute_ranged_spend;
    const allowed = Math.floor(room / info.threat);
    if (allowed < count) count = allowed;
  } else if (info.cls === Cls.Support) {
    let cap = Math.min(3, phase.supportCap + difficultyComp(w, 'support_alive_cap_delta'));
    // §8.4: aegis ≤ 2 before 13:00.
    if (info.id === 'enemy.aegis-medusa' && minuteFloat < 13) cap = Math.min(cap, 2);
    const room = cap - classAlive(w, Cls.Support);
    if (room < count) count = room;
  } else if (info.cls === Cls.Leech) {
    const room = phase.leechCap - classAlive(w, Cls.Leech);
    if (room < count) count = room;
  } else if (info.cls === Cls.Zone) {
    const room = phase.zoneCap + difficultyComp(w, 'zone_cap_delta') - classAlive(w, Cls.Zone);
    if (room < count) count = room;
  } else if (info.cls === Cls.Curtain) {
    const room = phase.curtainCap - classAlive(w, Cls.Curtain);
    if (room < count) count = room;
  }
  if (count < 1) return 0;

  // Single-archetype ceiling: ≤ 45% of alive threat after the spawn.
  if (info.cls !== Cls.Fodder) {
    const alive = aliveThreat(w);
    const same = archetypeThreat(w, info.id);
    while (count > 0 && alive + count * info.threat > 0 && (same + count * info.threat) / (alive + count * info.threat) > 0.45) count--;
  }
  return count < 0 ? 0 : count;
}

function archetypeThreat(w: World, defId: string): number {
  const list = w.enemies.liveIndices();
  let sum = 0;
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.is_boss && e.def_id === defId) sum += e.threat_score;
  }
  return sum;
}

// --- §8.5 formation geometry + §8.11 spawn safety ---

export function inView(w: World, x: number, y: number): boolean {
  // AMD-24 — the camera follows the player on a torus, so "on screen" is a wrapped offset.
  // Raw subtraction would call a point one unit across the seam 200 u away and skip its portal.
  return (
    Math.abs(wrapDeltaX(x - w.player.pos.x)) <= CAMERA_HALF_W &&
    Math.abs(wrapDeltaY(y - w.player.pos.y)) <= CAMERA_HALF_H
  );
}

/**
 * Fills `posX/posY/posTele` with up to `count` legal spawn points and returns how many are
 * usable. Every point is ≥ 12 u from the player and inside the arena; in-view points carry a
 * ≥ 1.5 s portal telegraph (§8.11).
 */
function buildFormation(w: World, form: Form, count: number, tick: number): number {
  if (count > posX.length) count = posX.length;
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  let n = 0;
  if (form === Form.Ring) {
    const r = 22 + w.rng.spawn.next() * 6;
    const base = w.rng.spawn.next() * Math.PI * 2;
    for (let i = 0; i < count; i++) {
      const a = base + (Math.PI * 2 * i) / count + (w.rng.spawn.next() - 0.5) * 0.15;
      n = tryPlace(w, px + Math.cos(a) * r, py + Math.sin(a) * r, n, 0);
    }
  } else if (form === Form.Stream) {
    const edge = Math.floor(w.rng.spawn.next() * 4);
    const along = (w.rng.spawn.next() * 2 - 1) * (edge < 2 ? w.arenaHalfW - 6 : w.arenaHalfH - 6);
    const inset = 2;
    for (let i = 0; i < count; i++) {
      const back = i * 2;
      let x: number;
      let y: number;
      if (edge === 0) {
        x = along;
        y = w.arenaHalfH - inset + back;
      } else if (edge === 1) {
        x = along;
        y = -w.arenaHalfH + inset - back;
      } else if (edge === 2) {
        x = w.arenaHalfW - inset + back;
        y = along;
      } else {
        x = -w.arenaHalfW + inset - back;
        y = along;
      }
      n = tryPlace(w, x, y, n, i * STREAM_ENTRY_TICKS);
    }
  } else if (form === Form.Pincer) {
    let hx = w.player.vel.x;
    let hy = w.player.vel.y;
    const hl = Math.sqrt(hx * hx + hy * hy);
    if (hl < 1e-4) {
      hx = 1;
      hy = 0;
    } else {
      hx /= hl;
      hy /= hl;
    }
    const heading = Math.atan2(hy, hx);
    const half = Math.ceil(count / 2);
    for (let side = 0; side < 2; side++) {
      const spread = ((100 + w.rng.spawn.next() * 40) * Math.PI) / 180;
      const a0 = heading + (side === 0 ? spread : -spread);
      const dist = 26 + w.rng.spawn.next() * 6;
      const cx = px + Math.cos(a0) * dist;
      const cy = py + Math.sin(a0) * dist;
      for (let i = 0; i < half && n < count; i++) {
        const jx = (w.rng.spawn.next() * 2 - 1) * 3;
        const jy = (w.rng.spawn.next() * 2 - 1) * 3;
        n = tryPlace(w, cx + jx, cy + jy, n, 0);
      }
    }
  } else {
    // Dispersed: Poisson-ish over the annulus 14–40 u, ≥ 2 u between spawns.
    let guard = 0;
    while (n < count && guard < count * 8) {
      guard++;
      const a = w.rng.spawn.next() * Math.PI * 2;
      const d = 14 + w.rng.spawn.next() * 26;
      const x = px + Math.cos(a) * d;
      const y = py + Math.sin(a) * d;
      let clash = false;
      for (let i = 0; i < n; i++) {
        if (wrapDist2(x, y, posX[i], posY[i]) < 4) {
          clash = true;
          break;
        }
      }
      if (clash) continue;
      n = tryPlace(w, x, y, n, 0);
    }
  }
  // Apply the in-view portal floor after every point is final.
  for (let i = 0; i < n; i++) {
    if (inView(w, posX[i], posY[i])) {
      const extra = posTele[i];
      posTele[i] = PORTAL_TELEGRAPH_TICKS + extra;
    }
  }
  return n;
}

function tryPlace(w: World, x: number, y: number, n: number, teleOffset: number): number {
  if (n >= posX.length) return n;
  // AMD-24 — no walls, so nothing is clamped inward; the point is canonicalised onto the torus.
  let cx = wrapX(x);
  let cy = wrapY(y);
  // §8.11 spawn safety measured the short way round. A raw delta would read a point 1 u across
  // the seam as 199 u away and let an enemy materialise on top of the player.
  const dx = wrapDeltaX(cx - w.player.pos.x);
  const dy = wrapDeltaY(cy - w.player.pos.y);
  const d2 = dx * dx + dy * dy;
  if (d2 < SPAWN_SAFE_RADIUS * SPAWN_SAFE_RADIUS) {
    // Push the point radially out to the safety ring. On a torus this always succeeds — there is
    // no wall for the pushed point to land in — so the old "drop it" branch is gone.
    const d = Math.sqrt(d2);
    const ux = d > 1e-4 ? dx / d : 1;
    const uy = d > 1e-4 ? dy / d : 0;
    cx = wrapX(w.player.pos.x + ux * (SPAWN_SAFE_RADIUS + 2));
    cy = wrapY(w.player.pos.y + uy * (SPAWN_SAFE_RADIUS + 2));
  }
  posX[n] = cx;
  posY[n] = cy;
  posTele[n] = teleOffset;
  return n + 1;
}

function emitFormation(w: World, st: DirectorRuntime, info: ArchetypeInfo, n: number, elite: boolean, affixA: string, affixB: string): number {
  let spawned = 0;
  const cap = enemyCap(w);
  for (let i = 0; i < n; i++) {
    if (w.enemies.count >= cap) break;
    const e = spawnEnemyExternal(w, info.id, posX[i], posY[i], elite, affixA, affixB, posTele[i]);
    if (e === null) break;
    st.spawn_serial++;
    spawned++;
  }
  return spawned;
}

// --- save / resume (A.6) ---

export function directorSnapshot(w: World): DirectorState {
  const st = runtime(w);
  return {
    version: 1,
    bank: st.bank,
    phase_index: st.phase_index,
    minute_index: st.minute_index,
    minute_spend: st.minute_spend,
    minute_elite_spend: st.minute_elite_spend,
    minute_ranged_spend: st.minute_ranged_spend,
    next_spawn_tick: st.next_spawn_tick,
    next_elite_tick: st.next_elite_tick,
    last_affix: st.last_affix,
    schedule_cursor: st.schedule_cursor,
    planned_start: st.planned_start.slice(),
    planned_end: st.planned_end.slice(),
    planned_env: st.planned_env.slice(),
    planned_count: st.planned_count,
    planned: st.planned ? 1 : 0,
    post_boss_until: st.post_boss_until,
    boss_active_until: st.boss_active_until,
    spawn_pause_until: st.spawn_pause_until,
    overpop_since: st.overpop_since,
    last_activity_tick: st.last_activity_tick,
    next_stalemate_scan: st.next_stalemate_scan,
    spawn_serial: st.spawn_serial,
    boss: bossSnapshot(w),
    hazards: hazardSnapshot(w),
  };
}

export function directorRestore(w: World, s: DirectorState): void {
  const st = runtime(w);
  st.bank = s.bank;
  st.phase_index = s.phase_index;
  st.minute_index = s.minute_index;
  st.minute_spend = s.minute_spend;
  st.minute_elite_spend = s.minute_elite_spend;
  st.minute_ranged_spend = s.minute_ranged_spend;
  st.next_spawn_tick = s.next_spawn_tick;
  st.next_elite_tick = s.next_elite_tick;
  st.last_affix = s.last_affix;
  st.schedule_cursor = s.schedule_cursor;
  for (let i = 0; i < st.planned_start.length; i++) {
    st.planned_start[i] = i < s.planned_start.length ? s.planned_start[i] : 0;
    st.planned_end[i] = i < s.planned_end.length ? s.planned_end[i] : 0;
    st.planned_env[i] = i < s.planned_env.length ? s.planned_env[i] : 1;
  }
  st.planned_count = s.planned_count;
  st.planned = s.planned !== 0;
  st.post_boss_until = s.post_boss_until;
  st.boss_active_until = s.boss_active_until;
  st.spawn_pause_until = s.spawn_pause_until;
  st.overpop_since = s.overpop_since;
  st.last_activity_tick = s.last_activity_tick;
  st.next_stalemate_scan = s.next_stalemate_scan;
  st.spawn_serial = s.spawn_serial;
  bossRestore(w, s.boss);
  hazardRestore(w, s.hazards);
}

/** Clear all director/boss/hazard state — call alongside `world.reset(seed)`. */
export function directorReset(w: World): void {
  const st = runtime(w);
  st.bank = 0;
  st.phase_index = 0;
  st.minute_index = 0;
  st.minute_spend = 0;
  st.minute_elite_spend = 0;
  st.minute_ranged_spend = 0;
  st.next_spawn_tick = 0;
  st.next_elite_tick = 0;
  st.last_affix = '';
  st.schedule_cursor = 0;
  st.planned_count = 0;
  st.planned = false;
  st.post_boss_until = 0;
  st.boss_active_until = 0;
  st.spawn_pause_until = 0;
  st.overpop_since = -1;
  st.last_activity_tick = 0;
  st.next_stalemate_scan = 0;
  st.spawn_serial = 0;
  for (let i = 0; i < st.spend_by_minute.length; i++) st.spend_by_minute[i] = 0;
  bossReset(w);
  hazardReset(w);
}

/** Exposed for the §8.7 intro-framing assertion in tests. */
export { BOSS_INTRO_TICKS };
